import { useQuery } from "@tanstack/react-query";
import { LoadError } from "@/components/ui/LoadError";
import { DataAttributionNote } from "@/components/DataAttributionNote";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Flag, ChevronRight, CalendarClock, Layers, Plus } from "lucide-react";
import { useState } from "react";
import { getDashboard } from "@/api/dashboard";
import { getProfile } from "@/api/profile";
import { getWeeklyCheckinStatus } from "@/api/body";
import { WeeklyCheckinModal } from "@/components/body/WeeklyCheckinModal";
import { Modal } from "@/components/ui/Modal";
import { ManualWorkoutForm } from "@/components/workouts/ManualWorkoutForm";
import { Scale as ScaleIcon } from "lucide-react";
import { Card, SectionLabel } from "@/components/ui/Card";
import { CoachSaysCard } from "@/components/coach/CoachSaysCard";
import { PmcCard } from "@/components/dashboard/PmcCard";
import { ReadinessCard } from "@/components/dashboard/ReadinessCard";
import { WorkoutRow } from "@/components/dashboard/WorkoutRow";
import { CurrentWeekPreview } from "@/components/dashboard/CurrentWeekPreview";
import { EventsWidget } from "@/components/dashboard/EventsWidget";
import { WeightTracker } from "@/components/dashboard/WeightTracker";
import { CalorieBalanceCard } from "@/components/nutrition/CalorieBalanceCard";
import { DailyCheckCard } from "@/components/dashboard/DailyCheckCard";
import { EmptyPlanCTA } from "@/components/dashboard/EmptyPlanCTA";
import { fmtDate, fmtLongDate } from "@/lib/dates";
import { fmtDuration, SPORT_LABELS } from "@/lib/tss";

function greetingKey(hour: number): string {
  if (hour < 5) return "dashboard.greeting_night";
  if (hour < 12) return "dashboard.greeting_morning";
  if (hour < 18) return "dashboard.greeting_afternoon";
  return "dashboard.greeting_evening";
}

export default function Dashboard() {
  const { t } = useTranslation();

  const profileQ = useQuery({ queryKey: ["profile"], queryFn: getProfile });
  const dashQ = useQuery({ queryKey: ["dashboard"], queryFn: getDashboard });
  const checkinQ = useQuery({
    queryKey: ["weekly-checkin"],
    queryFn: getWeeklyCheckinStatus,
    staleTime: 60_000,
  });
  const [checkinOpen, setCheckinOpen] = useState(false);
  // Phase 24.7.3 T3 — manual workout entry modal
  const [manualWorkoutOpen, setManualWorkoutOpen] = useState(false);

  if (profileQ.isLoading || dashQ.isLoading) {
    return <div className="py-10 text-center text-taupe">{t("common.loading")}</div>;
  }

  if (profileQ.isError || dashQ.isError || !profileQ.data || !dashQ.data) {
    return <LoadError retrying={profileQ.isFetching || dashQ.isFetching} onRetry={() => {
      void profileQ.refetch();
      void dashQ.refetch();
    }} />;
  }

  if (!profileQ.data.plan_instantiated) {
    // Phase 14.3 — onboarded but no plan yet. The old nav("/setup")
    // looped (/setup -> /onboarding -> already onboarded -> /). Render a
    // create-plan CTA instead.
    return <EmptyPlanCTA />;
  }

  const profile = profileQ.data;
  const dash = dashQ.data;
  const firstName = (profile.name || "").split(" ")[0] || t("common.athlete_fallback");
  const currentWeekNum = dash.current_week?.week_number ?? null;

  return (
    <div className="mx-auto w-full space-y-6">
      {/* ===== Page header ===== */}
      <header>
        <SectionLabel>{fmtLongDate(new Date())}</SectionLabel>
        <h1 className="mt-1 font-serif text-[28px] font-medium leading-tight">
          {t(greetingKey(new Date().getHours()), { name: firstName })}
        </h1>
        {dash.race_countdown_days != null ? (
          <div className="mt-1 flex items-center gap-1.5 text-[13px] text-taupe">
            <Flag className="h-3.5 w-3.5 text-amber-dark" strokeWidth={1.8} />
            <span className="text-navy-deep">
              {t("dashboard.days_to_race", { count: dash.race_countdown_days })}
            </span>
          </div>
        ) : null}
      </header>

      {checkinQ.data?.needed ? (
        <button
          type="button"
          onClick={() => setCheckinOpen(true)}
          className="flex w-full items-center justify-between gap-3 rounded-2xl border border-amber/50 bg-amber/15 px-5 py-4 text-left transition-colors hover:bg-amber/25"
        >
          <div className="flex items-start gap-3">
            <ScaleIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-dark" strokeWidth={1.8} />
            <div>
              <div className="text-[10px] font-medium uppercase tracking-[1.5px] text-amber-dark">
                {t("dashboard.weekly_checkin_title")}
              </div>
              <div className="mt-0.5 text-[14px] font-medium text-navy-deep">
                {t("dashboard.weekly_checkin_cta")}
              </div>
              <div className="mt-0.5 text-[12px] text-taupe">
                {t("dashboard.weekly_checkin_desc")}
                {checkinQ.data.days_since_last_log != null
                  ? " " + t("dashboard.weekly_checkin_last", { days: checkinQ.data.days_since_last_log })
                  : ""}
              </div>
            </div>
          </div>
          <span className="shrink-0 rounded-md bg-navy px-3 py-1.5 text-[12px] font-medium text-cream-light">
            {t("dashboard.weekly_checkin_fill")}
          </span>
        </button>
      ) : null}

      {/* ===== 3-column grid on desktop ===== */}
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-[1fr_1.3fr_1fr]">
        {/* ---- LEFT — Athlete state ---- */}
        <section className="space-y-4">
          <PmcCard
            pmc={dash.pmc}
            currentWeekNumber={currentWeekNum}
            currentPhase={dash.current_week?.phase ?? null}
          />
          <ReadinessCard />

          <CalorieBalanceCard variant="compact" />
          <WeightTracker weight={dash.weight} />
          <EventsWidget events={dash.upcoming_events ?? []} />
        </section>

        {/* ---- MIDDLE — Training ---- */}
        <section className="space-y-4">
          {dash.next_workout ? <NextWorkoutCard dash={dash} /> : null}
          {dash.current_week && dash.current_week.workouts.length > 0 ? (
            <CurrentWeekPreview
              weekNumber={dash.current_week.week_number}
              phase={dash.current_week.phase}
              startDate={dash.current_week.start_date}
              endDate={dash.current_week.end_date}
              targetTss={dash.current_week.target_tss}
              actualTss={dash.current_week.actual_tss}
              completionPct={dash.current_week.completion_pct}
              workouts={dash.current_week.workouts}
            />
          ) : null}
          <Card>
            <div className="mb-2 flex items-center justify-between">
              <SectionLabel>{t("dashboard.recent_workouts")}</SectionLabel>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setManualWorkoutOpen(true)}
                  className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-dark hover:underline"
                >
                  <Plus className="h-3 w-3" strokeWidth={2.4} />
                  {t("workout.manual.button_add")}
                </button>
                {dash.recent_workouts && dash.recent_workouts.length > 0 ? (
                  <Link
                    to="/history"
                    className="text-[11px] font-medium text-amber-dark hover:underline"
                  >
                    {t("common.all_arrow")}
                  </Link>
                ) : null}
              </div>
            </div>
            {dash.recent_workouts && dash.recent_workouts.length > 0 ? (
              <div className="space-y-2">
                {dash.recent_workouts.slice(0, 4).map((w) => (
                  <WorkoutRow key={w.id} workout={w} />
                ))}
              </div>
            ) : (
              <div className="text-[12px] text-taupe">
                {t("dashboard.recent_workouts_empty")}
              </div>
            )}
          </Card>
        </section>

        {/* ---- RIGHT — Coach insights ---- */}
        <section className="space-y-4">
          <CoachSaysCard
            message={dash.advisor.message}
            status={dash.advisor.status}
            narrative={dash.advisor.narrative}
            fullReviewId={dash.advisor.full_review_id}
          />
          <DailyCheckCard check={dash.latest_daily_check} />
        </section>
      </div>

      {checkinOpen ? (
        <WeeklyCheckinModal
          onClose={() => setCheckinOpen(false)}
          onSaved={() => setCheckinOpen(false)}
        />
      ) : null}
      <Modal
        isOpen={manualWorkoutOpen}
        onClose={() => setManualWorkoutOpen(false)}
        title={t("workout.manual.modal_title")}
      >
        <ManualWorkoutForm onClose={() => setManualWorkoutOpen(false)} />
      </Modal>
      <DataAttributionNote />
    </div>
  );
}

// ---- Next-workout card (kept from phase10-13, slimmed to fit 3-col) -----

function NextWorkoutCard({ dash }: { dash: ReturnType<typeof useQuery<any, any>>["data"] }) {
  const { t } = useTranslation();
  const nw = dash.next_workout!;
  return (
    <Link
      to={`/workout/planned/${nw.id}`}
      className="group block rounded-2xl border border-cream-border bg-cream-light p-5 shadow-soft transition-colors hover:border-amber/50"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <SectionLabel>{t("dashboard.next_workout")}</SectionLabel>
          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[13px] text-taupe">
            <CalendarClock className="h-3.5 w-3.5 text-amber-dark" strokeWidth={1.8} />
            <span className="font-medium text-navy-deep capitalize">
              {nw.relative_time}
            </span>
            <span>·</span>
            <span className="text-navy-deep">{nw.day_of_week}</span>
            <span>·</span>
            <span>{fmtDate(nw.date, "d MMMM")}</span>
          </div>
          <h3 className="mt-2 font-serif text-[19px] font-medium leading-snug text-navy-deep">
            {nw.title}
          </h3>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-taupe">
            <span>{SPORT_LABELS[nw.sport] ?? nw.sport}</span>
            <span>·</span>
            <span>{nw.type}</span>
            <span>·</span>
            <span className="metric-number text-[13px] text-navy-deep">
              {fmtDuration(nw.duration_minutes)}
            </span>
            <span>·</span>
            <span className="metric-number text-[13px] text-navy-deep">
              TSS {nw.target_tss}
            </span>
            {nw.is_compound ? (
              <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-amber/20 px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.15em] text-amber-dark">
                <Layers className="h-3 w-3" strokeWidth={2} />
                compound
              </span>
            ) : null}
          </div>
        </div>
        <ChevronRight
          className="mt-1 h-5 w-5 shrink-0 text-taupe transition-colors group-hover:text-amber-dark"
          strokeWidth={1.8}
        />
      </div>
    </Link>
  );
}
